import { isPapertradeError } from "../sdk";
import type { Refusal } from "../sdk";

/** What a ticket refusal says, short enough for a button. */
export const REFUSAL: Record<Refusal, string> = {
  loading: "Loading markets",
  badAmount: "Enter a valid amount",
  noMargin: "Enter margin",
  noWallet: "Connect a wallet",
  noAccount: "Waiting for your account",
  tradingPaused: "Trading is paused",
  marketClosed: "Market is close-only",
  intakePaused: "Opens are paused",
  belowMinimumMargin: "Below minimum margin",
  belowMinimumNotional: "Below minimum size",
  aboveMaximumPosition: "Above market max size",
  leverageTooHigh: "Leverage above market max",
  insufficientFunds: "Not enough balance",
  queueLiquidity: "Queue can't fund this yet",
  openInterest: "Side is at its OI cap",
};

/** One line for any failure, in the trader's words. */
export function describe(error: unknown): string {
  if (!isPapertradeError(error)) return error instanceof Error ? error.message : String(error);
  switch (error.kind) {
    case "declined":
      return "Declined in your wallet";
    case "wrongChain":
      return "Your wallet is on another network";
    case "wrongWallet":
      return "The connected wallet is not this account's owner";
    case "needsKey":
      return error.state === "unregistered" ? "Enable trading first" : "Enable trading first";
    case "refused":
      return (error.code && REFUSAL[error.code as Refusal]) || error.message;
    case "rateLimited":
      return "Too many requests, try again in a moment";
    case "relayer":
      return error.reason || error.code || error.message;
    case "notKept":
      return `The relayer did not keep it (${error.state ?? "unknown"})`;
    case "network":
      return "Network error: the relayer did not answer. Check your positions before retrying.";
    case "noAccount":
      return "Your account has not loaded yet";
    default:
      return error.message;
  }
}

/** Whether a failure was the trader saying no: shown quietly, if at all. */
export const declined = (error: unknown) => isPapertradeError(error) && error.kind === "declined";
